/* Compound Interest Calculator — Ch. 6
   Inputs: principal, monthly contribution, annual return, years, compounding mode.
   Renders results, area chart (compound vs simple), insight box.
*/
(function () {
  'use strict';

  function fmtMoney(n) {
    return '$' + Math.round(n).toLocaleString('en-US');
  }

  class CompoundInterest {
    constructor(containerId) {
      this.container = document.getElementById(containerId);
      if (!this.container) return;
      this.state = Object.assign({
        principal: 1000, monthly: 50, rate: 8, years: 30, comp: 'monthly'
      }, this.loadFromURL());
      this.render();
      this.attach();
      this.update();
    }

    loadFromURL() {
      var p = IT.url.read();
      var s = {};
      if (p.p)  s.principal = +p.p;
      if (p.m)  s.monthly   = +p.m;
      if (p.r)  s.rate      = +p.r;
      if (p.y)  s.years     = +p.y;
      if (p.c)  s.comp      = p.c;
      return s;
    }
    saveToURL() {
      IT.url.write({ p: this.state.principal, m: this.state.monthly, r: this.state.rate, y: this.state.years, c: this.state.comp });
    }

    render() {
      this.container.innerHTML = `
        <div class="sim-layout">
          <div class="sim-controls">
            ${field('Starting amount', 'principal', 'range', { min: 0, max: 10000, step: 100, val: this.state.principal, fmt: fmtMoney })}
            ${field('Monthly contribution', 'monthly', 'range', { min: 0, max: 1000, step: 10, val: this.state.monthly, fmt: fmtMoney })}
            ${field('Annual return', 'rate', 'range', { min: 1, max: 20, step: 0.25, val: this.state.rate, fmt: v => v + '%' })}
            ${field('Years', 'years', 'range', { min: 1, max: 50, step: 1, val: this.state.years, fmt: v => v + ' yr' })}
            <div class="sim-field">
              <span class="sim-field__label">Compounding</span>
              <div class="sim-toggle" data-toggle="comp">
                <button data-val="monthly" class="${this.state.comp === 'monthly' ? 'is-active' : ''}">Monthly</button>
                <button data-val="annually" class="${this.state.comp === 'annually' ? 'is-active' : ''}">Annually</button>
              </div>
            </div>
          </div>
          <div class="sim-results">
            <div class="sim-result">
              <div class="sim-result-label">Final balance</div>
              <div class="sim-result-primary" data-out="final">$0</div>
              <div class="sim-result__sub" data-out="multi"></div>
            </div>
            <div style="display:grid; grid-template-columns:1fr 1fr; gap:16px;">
              <div class="sim-result"><div class="sim-result-label">What you put in</div><div style="font-family:var(--font-display); font-weight:700; font-size:22px;" data-out="contrib">$0</div></div>
              <div class="sim-result"><div class="sim-result-label">Interest earned</div><div style="font-family:var(--font-display); font-weight:700; font-size:22px;" data-out="interest">$0</div></div>
            </div>
            <div class="sim-chart-wrap"><canvas id="ci-chart"></canvas></div>
            <div data-out="insights"></div>
          </div>
        </div>
      `;
      bindRanges(this.container, this.state, () => this.update());
    }

    attach() {
      this.container.querySelectorAll('[data-toggle="comp"] button').forEach(b => {
        b.addEventListener('click', () => {
          this.state.comp = b.dataset.val;
          this.container.querySelectorAll('[data-toggle="comp"] button').forEach(x => x.classList.remove('is-active'));
          b.classList.add('is-active');
          this.update();
        });
      });
    }

    calculate(years, principal, monthly, rate, comp) {
      var n = comp === 'monthly' ? 12 : 1;
      var r = rate / 100;
      var i = r / n;
      var totalPeriods = n * years;
      var compound = principal * Math.pow(1 + i, totalPeriods);
      var pmt = comp === 'monthly' ? monthly : monthly * 12;
      var contribFV = pmt * (i === 0 ? totalPeriods : ((Math.pow(1 + i, totalPeriods) - 1) / i));
      var fv = compound + contribFV;
      var contributed = principal + monthly * 12 * years;
      var simple = principal * (1 + r * years) + contributed - principal;
      return { fv: fv, contributed: contributed, interest: fv - contributed, simple: simple };
    }

    update() {
      var s = this.state;
      var res = this.calculate(s.years, s.principal, s.monthly, s.rate, s.comp);
      var $ = sel => this.container.querySelector(sel);
      this.container.querySelector('[data-out="final"]').classList.add('sim-result-primary');
      $('[data-out="final"]').textContent    = fmtMoney(res.fv);
      $('[data-out="contrib"]').textContent  = fmtMoney(res.contributed);
      $('[data-out="interest"]').textContent = fmtMoney(res.interest);
      var multi = res.contributed > 0 ? (res.fv / res.contributed) : 0;
      $('[data-out="multi"]').textContent = `Your money worked ${multi.toFixed(1)}× harder than you did`;

      // insights
      var doublesIn = 72 / s.rate;
      var diff5  = res.fv - this.calculate(Math.max(0, s.years - 5),  s.principal, s.monthly, s.rate, s.comp).fv;
      var diff10 = res.fv - this.calculate(Math.max(0, s.years - 10), s.principal, s.monthly, s.rate, s.comp).fv;
      $('[data-out="insights"]').innerHTML = `
        <div class="sim-insight">At ${s.rate}%, your money doubles every ${doublesIn.toFixed(1)} years (Rule of 72).</div>
        <div class="sim-insight">Starting today vs. 5 years from now: <strong>${fmtMoney(diff5)}</strong> difference.</div>
        <div class="sim-insight">Starting today vs. 10 years from now: <strong>${fmtMoney(diff10)}</strong> difference.</div>
      `;
      this.renderChart(s, res);
      this.saveToURL();
    }

    renderChart(s, res) {
      if (!window.Chart) { setTimeout(() => this.renderChart(s, res), 200); return; }
      var labels = [], compoundData = [], simpleData = [];
      for (var y = 0; y <= s.years; y++) {
        labels.push(y);
        var pt = this.calculate(y, s.principal, s.monthly, s.rate, s.comp);
        compoundData.push(pt.fv);
        simpleData.push(pt.simple);
      }
      var ctx = this.container.querySelector('#ci-chart');
      if (this._chart) this._chart.destroy();
      var T = window.IT_TOKENS;
      this._chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            { label: 'Compound', data: compoundData, borderColor: T.coral, backgroundColor: T.coralFill, borderWidth: 2, fill: true, pointRadius: 0, tension: 0.25 },
            { label: 'Simple',   data: simpleData,   borderColor: T.creamMid, borderDash: [6, 4], borderWidth: 1.5, fill: false, pointRadius: 0, tension: 0.25 }
          ]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          scales: {
            x: { title: { display: true, text: 'Years' } },
            y: { ticks: { callback: v => '$' + (v >= 1000 ? (v/1000).toFixed(0) + 'k' : v) } }
          }
        }
      });
    }
  }

  // ------ helpers shared with other simulators ------
  function field(label, key, type, opts) {
    var attrs = type === 'range'
      ? `min="${opts.min}" max="${opts.max}" step="${opts.step}" value="${opts.val}"`
      : `value="${opts.val}"`;
    var display = (opts.fmt ? opts.fmt(opts.val) : opts.val);
    return `
      <div class="sim-field">
        <label class="sim-field__label" for="f-${key}">
          <span>${label}</span>
          <span class="val" data-display="${key}">${display}</span>
        </label>
        <input type="${type}" id="f-${key}" data-state="${key}" ${attrs}>
      </div>`;
  }
  function bindRanges(root, state, onChange) {
    root.querySelectorAll('input[data-state]').forEach(input => {
      input.addEventListener('input', () => {
        var k = input.dataset.state;
        var v = input.type === 'number' || input.type === 'range' ? +input.value : input.value;
        state[k] = v;
        var label = root.querySelector(`[data-display="${k}"]`);
        if (label) {
          if (k === 'rate' || k === 'returnPct') label.textContent = v + '%';
          else if (k === 'years' || k === 'age')  label.textContent = v + (k === 'age' ? '' : ' yr');
          else                                    label.textContent = '$' + Math.round(v).toLocaleString('en-US');
        }
        onChange();
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => new CompoundInterest('simulator-container'));
})();
